import React, { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Flag, Loader2, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";

const LABEL = { harassment: "Harassment", hate: "Hate", sexual: "Sexual content", violence: "Violence", spam: "Spam", impersonation: "Impersonation", confidential: "Confidential leak", other: "Other" };

// Command Deck → Reports: officers review member reports (dismiss or remove the content)
export default function ReportsTab() {
  const [status, setStatus] = useState("open");
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/reports", { params: { status } }).then((r) => setList(r.data.reports)).catch(() => {});
  useEffect(() => { load(); }, [status]); // eslint-disable-line
  const resolve = async (r, action) => {
    if (action === "remove" && !window.confirm(`Remove this ${r.target_type} for everyone?`)) return;
    setBusy(r.id);
    try { await api.post(`/admin/reports/${r.id}/resolve`, { action }); toast.success(action === "remove" ? "Content removed" : "Report dismissed"); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  return (
    <div data-testid="reports-tab">
      <div className="mb-4 flex gap-2">
        {["open", "resolved"].map((s) => <button key={s} onClick={() => setStatus(s)} className={`rounded-full px-4 py-1.5 text-sm font-600 capitalize transition-colors ${status === s ? "bg-[hsl(var(--gold))] text-[hsl(var(--primary-foreground))]" : "bg-secondary text-muted-foreground hover:text-foreground"}`} data-testid={`reports-filter-${s}`}>{s}</button>)}
      </div>
      {list.length === 0 && <Card className="gold-ring glass p-8 text-center text-sm text-muted-foreground" data-testid="reports-empty"><ShieldCheck className="mx-auto mb-2 h-6 w-6 text-emerald-500" />No {status} reports — the Mess is in good order.</Card>}
      <div className="space-y-3">
        {list.map((r) => (
          <Card key={r.id} className="gold-ring glass p-4" data-testid={`report-row-${r.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="flex items-center gap-2 text-sm font-700"><Flag className="h-4 w-4 text-[hsl(var(--gold))]" />{r.target_type.toUpperCase()} by {r.snapshot?.author_name || "unknown"} <Badge variant="outline" className="border-amber-500/50 text-amber-500">{LABEL[r.reason] || r.reason}</Badge>{r.frigate && <Badge variant="outline">{r.frigate}</Badge>}</p>
                {r.snapshot?.excerpt && <p className="mt-1 rounded-md bg-secondary/60 p-2 text-sm italic">“{r.snapshot.excerpt}”</p>}
                {r.note && <p className="mt-1 text-xs text-muted-foreground">Reporter's note: {r.note}</p>}
                <p className="mt-1 text-[0.65rem] text-muted-foreground">Reported by {r.reporter_name} · {new Date(r.created_at).toLocaleString()}{r.status === "resolved" && ` · ${r.action === "remove" ? "removed" : "dismissed"} by ${r.resolved_by}`}</p>
              </div>
              {r.status === "open" && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="gold-ring" disabled={busy === r.id} onClick={() => resolve(r, "dismiss")} data-testid={`report-dismiss-${r.id}`}>Dismiss</Button>
                  {r.target_type !== "member" && <Button size="sm" variant="destructive" disabled={busy === r.id} onClick={() => resolve(r, "remove")} data-testid={`report-remove-${r.id}`}>{busy === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="mr-1 h-3.5 w-3.5" />Remove content</>}</Button>}
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
